import React, { useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFlow } from '../../contexts/FlowContext';
import { FlowDivider } from '../components/FlowDivider';
import { FlowInputError } from '../components/FlowInputError';
import { FlowLoadingOverlay } from '../components/FlowLoadingOverlay';
import { FlowPrimaryButton } from '../components/FlowPrimaryButton';
import { FlowTextField } from '../components/FlowTextField';
import { SocialLoginButtons } from '../components/SocialLoginButtons';
import { validateEmail, validatePassword } from '../authValidation';
import { FlowAuthMethod, useFlowAuth } from '../hooks/useFlowAuth';
import { ChatbooksAuthResult } from '../../utils/chatbooksAuth';
import { StudioFlowColors, StudioFlowRadii } from '../studioFlowTheme';

export const SignInScreen: React.FC = () => {
  const formId = 'studio-web-sign-in-form';
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { state, updateState } = useFlow();
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });

  const persistAuth = async (
    authResult: ChatbooksAuthResult,
    authMethod: FlowAuthMethod,
  ) => {
    updateState({
      authMethod,
      email: authResult.email,
      accessToken: authResult.accessToken,
      personId: authResult.personId,
      displayName: authResult.displayName || '',
      chatbooksEnvironment: authResult.environment,
      password: '',
      sessionId: '',
      sessionKey: '',
    });
    navigate('/phone');
  };

  const {
    handleSignIn,
    handleSSO,
    isBusy,
    busyMessage,
    externalError,
  } = useFlowAuth({ onAuthenticated: persistAuth });

  const emailValidationError = touched.email ? validateEmail(state.email) : null;
  const passwordValidationError = touched.password ?
    validatePassword(state.password, 1) :
    null;

  const canSubmit =
    !validateEmail(state.email) &&
    !validatePassword(state.password, 1) &&
    !isBusy;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched({ email: true, password: true });
    setSubmitError('');

    if (!canSubmit) {
      return;
    }

    const result = await handleSignIn({
      email: state.email.trim(),
      password: state.password,
    });

    if (result?.error) {
      setSubmitError(result.error);
    }
  };

  const displayError = submitError || externalError;

  const signUpLink = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'baseline',
        gap: 1,
        my: 2,
      }}
    >
      <Typography
        sx={{
          color: StudioFlowColors.subtitle,
          fontSize: 13,
          letterSpacing: '-0.13px',
          fontFamily: StudioFlowColors.bodyFontFamily,
        }}
      >
        Don&apos;t have an account?
      </Typography>
      <Link
        component={RouterLink}
        to="/signup"
        underline="none"
        sx={{
          color: StudioFlowColors.title,
          fontWeight: 700,
          fontSize: 15,
          letterSpacing: '-0.15px',
          fontFamily: StudioFlowColors.bodyFontFamily,
        }}
      >
        Sign up
      </Link>
    </Box>
  );

  const formFields = (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box>
        <FlowTextField
          placeholder="Email address"
          type="email"
          autoComplete="email"
          value={state.email}
          onChange={(event) => updateState({ email: event.target.value })}
          onBlur={() => setTouched((current) => ({ ...current, email: true }))}
          inputProps={{ 'aria-label': 'Email address' }}
          data-testid="signin-input-email"
        />
        {emailValidationError && <FlowInputError message={emailValidationError} />}
      </Box>
      <Box>
        <FlowTextField
          placeholder="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={state.password}
          onChange={(event) => updateState({ password: event.target.value })}
          onBlur={() => setTouched((current) => ({ ...current, password: true }))}
          inputProps={{ 'aria-label': 'Password' }}
          data-testid="signin-input-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Toggle password visibility"
                  onClick={() => setShowPassword((current) => !current)}
                  edge="end"
                  sx={{ color: StudioFlowColors.title }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {passwordValidationError && <FlowInputError message={passwordValidationError} />}
      </Box>
    </Box>
  );

  const signInButton = (
    <FlowPrimaryButton type="submit" form={formId} disabled={!canSubmit}>
      {isBusy ? 'Signing in…' : 'Sign in'}
    </FlowPrimaryButton>
  );

  const socialSection = (
    <>
      <FlowDivider label="Or, continue with" />
      <SocialLoginButtons
        onGoogle={() => handleSSO('google')}
        disabled={isBusy}
      />
    </>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: StudioFlowColors.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {!isDesktop && <Box sx={{ height: 24 }} />}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: isDesktop ? 'center' : 'flex-start',
          px: { xs: 3, md: 4 },
          pt: { xs: 7, md: 6 },
          pb: { xs: 14, md: 6 },
        }}
      >
        <Box
          component="form"
          id={formId}
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            maxWidth: isDesktop ? 566 : 402,
            ...(isDesktop && {
              bgcolor: StudioFlowColors.background,
              borderRadius: `${StudioFlowRadii.card}px`,
              p: 6,
            }),
          }}
        >
          <Typography
            sx={{
              fontFamily: StudioFlowColors.displayFontFamily,
              color: StudioFlowColors.title,
              fontWeight: 400,
              fontSize: { xs: 34, md: 44 },
              lineHeight: 1,
              textAlign: 'center',
              mb: { xs: 3, md: 4 },
            }}
          >
            Sign in to use Chatbooks Studio
          </Typography>

          <Box sx={{ maxWidth: 354, mx: isDesktop ? 'auto' : 0, width: '100%' }}>
            {formFields}
            {signUpLink}
            {displayError && (
              <FlowInputError message={displayError} />
            )}

            {isDesktop ? (
              <>
                <Box sx={{ mt: displayError ? 2 : 0 }}>{signInButton}</Box>
                <Box sx={{ mt: 3 }}>{socialSection}</Box>
              </>
            ) : (
              <>
                {socialSection}
              </>
            )}
          </Box>
        </Box>
      </Box>
      {!isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            bgcolor: StudioFlowColors.background,
            px: 3,
            pt: 2,
            pb: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          {signInButton}
        </Box>
      )}
      {isBusy && (
        <FlowLoadingOverlay
          message={busyMessage || 'Signing you in'}
          fullScreen
        />
      )}
    </Box>
  );
};
